/**
 * Page-element scraper. Walks the visible DOM on the host page and
 * returns a small, prompt-friendly inventory the agent can reference
 * via `highlightElement`.
 *
 * Why text-content (not selectors): host pages re-skin frequently;
 * a CSS selector chain is brittle. Sending `{id, tagName, text}` lets
 * the agent point at *what the visitor sees* and the widget locates
 * the element by substring scan at highlight time.
 *
 * Budget rules — keep prompt size sane:
 *   - Max 50 elements per scrape (`MAX_ELEMENTS`)
 *   - Each text trimmed + truncated to 200 chars
 *   - Skip any element shorter than `MIN_TEXT_LEN` chars
 *   - Skip elements that aren't visible (display:none, off-screen,
 *     zero-width)
 *
 * FIGURES (2026-10-24). The prose pass above cannot see a price. On a
 * typical pricing page the amount is markup like
 * `<div class="price"><b>$290</b><span>/ month</span></div>`: the `div`
 * and `b` are outside `SCRAPED_TAGS`, and `$290` is under
 * `MIN_TEXT_LEN` anyway. So the agent was handed a pricing page with
 * every plan name and feature and NO amounts — and on a prod voice call
 * it filled the hole from nothing, quoting "$290 per month for up to ten
 * users" on a page that says per workspace with unlimited users, then
 * "$790" for a plan that costs $890. Inventing a customer's prices is
 * about the worst thing an agent can do on their own site, so a second
 * pass carries currency amounts and percentages with their own small
 * budget, independent of the 50 above. It takes the OUTERMOST short
 * element holding each figure, which is what keeps the unit attached:
 * "$290/ month", never a bare "$290".
 *
 * The returned `id` is the widget's own monotonic counter — stable
 * within a single scrape, NOT a DOM id. Use it as a lookup hint when
 * highlight events come back from the server, but always re-locate
 * by `text` for resilience.
 */

import type { PageElement } from '@showpilot/core';

const SCRAPED_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'li',
  'button',
  'a',
]);

/**
 * Where a figure hides when it isn't prose. Deliberately NOT a general
 * widening of `SCRAPED_TAGS`: every ancestor `div` repeats all of its
 * descendants' text, so scraping divs wholesale would flood the
 * inventory with duplicated wrapper text. These tags only ever enter
 * through the figure pass, which is gated on `FIGURE_RE` and a short
 * length.
 */
const FIGURE_TAGS = 'div,span,b,strong,dd,td,th';

/**
 * The climb may only pass a parent whose other children are inline, so
 * it picks up a unit ("$290" + "/ month") and never glues two blocks
 * together: `textContent` inserts no whitespace, so climbing over
 * `<td>Non-profit discount</td><td>40%</td>` would read
 * "Non-profit discount40%". Keeping the text exactly as `textContent`
 * renders it also keeps `findElementByText` able to re-locate the entry.
 */
const INLINE_TAGS = new Set([
  'span',
  'b',
  'strong',
  'em',
  'i',
  'small',
  'sup',
  'sub',
  'abbr',
  's',
  'u',
  'bdi',
  'time',
  'mark',
]);

/**
 * A currency amount or a percentage — the class of fact an agent must
 * never improvise. Symbol on either side (en "$290", fr "290 €"), the
 * common ISO codes, and percents.
 */
const FIGURE_RE = /[$€£¥₹]\s?\d|\d\s?[$€£¥₹]|\d\s?(?:usd|eur|gbp|cad|aud)\b|\d\s?%/i;

const MAX_ELEMENTS = 50;
const MIN_TEXT_LEN = 6;
const MAX_TEXT_LEN = 200;
/**
 * Figure budget. Figures are carried INSIDE the MAX_ELEMENTS total — when
 * the prose pass has already spent the budget, the trailing prose makes
 * room for them (2026-10-29: "on top of" sent 62 elements and the chat
 * contract rejected the whole turn; text chat was down on pricing pages).
 */
const MAX_FIGURES = 12;
/** "$290" is four characters and is the whole point. */
const FIGURE_MIN_TEXT_LEN = 2;
/** Long enough for "$890 / month", short enough to exclude prose. */
const FIGURE_MAX_TEXT_LEN = 80;
/** How far a figure may climb to pick up its unit. Bounded, not clever. */
const FIGURE_CLIMB_MAX = 3;

export type ScrapeOptions = {
  /** Defaults to `document.body`. Tests can pass a fixture root. */
  root?: Element | Document;
};

function visibleText(el: HTMLElement): string {
  return (el.textContent ?? '').trim().replace(/\s+/g, ' ');
}

/** True when the element or any ancestor inside `root` is hidden. */
function hiddenInChain(el: HTMLElement, root: Element | Document): boolean {
  let node: HTMLElement | null = el;
  while (node && root.contains(node)) {
    if (!isVisible(node)) return true;
    node = node.parentElement;
  }
  return false;
}

/** True when every element child of `el` is an inline tag. */
function onlyInlineChildren(el: HTMLElement): boolean {
  for (const child of Array.from(el.children)) {
    if (!INLINE_TAGS.has(child.tagName.toLowerCase())) return false;
  }
  return true;
}

export function scrapePageElements(opts: ScrapeOptions = {}): PageElement[] {
  if (typeof window === 'undefined') return [];
  const root = opts.root ?? document;

  const out: PageElement[] = [];
  const takenText = new Set<string>();
  const proseEls: HTMLElement[] = [];
  let counter = 0;

  const push = (el: HTMLElement, text: string): PageElement => {
    const tagName = el.tagName.toLowerCase();
    const entry: PageElement = {
      id: `sp-pe-${counter++}`,
      tagName,
      text: text.length > MAX_TEXT_LEN ? `${text.slice(0, MAX_TEXT_LEN - 3)}...` : text,
    };
    // Capture href for anchors so the agent has navigation targets it
    // can cite. Only same-host paths (or root-relative) are useful for
    // navigation; cross-origin links shouldn't be navigated to. Strip
    // hash/query for prompt size; full hrefs aren't needed.
    if (tagName === 'a') {
      const href = (el as HTMLAnchorElement).getAttribute('href');
      if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
        entry.href = href.length > 200 ? href.slice(0, 200) : href;
      }
    }
    out.push(entry);
    takenText.add(entry.text);
    return entry;
  };

  for (const el of root.querySelectorAll<HTMLElement>(Array.from(SCRAPED_TAGS).join(','))) {
    if (out.length >= MAX_ELEMENTS) break;
    if (!isVisible(el)) continue;
    const text = visibleText(el);
    if (text.length < MIN_TEXT_LEN) continue;
    push(el, text);
    proseEls.push(el);
  }
  const proseCount = out.length;

  // Figure pass. Runs over the whole document on its own budget, so a
  // price is still carried when the prose pass has already spent all 50
  // entries — which is exactly what happens on a pricing page.
  //
  // Start from the INNERMOST element holding each figure (`<b>$290</b>`)
  // and climb while the parent stays short and adds nothing the prose
  // pass already captured. That lands on `<div class="price">` —
  // "$290/ month", the amount with its unit — and stops at the plan card,
  // whose text is the heading and feature line we already have.
  const holders = Array.from(root.querySelectorAll<HTMLElement>(`${FIGURE_TAGS},${Array.from(SCRAPED_TAGS).join(',')}`))
    .filter((el) => FIGURE_RE.test(visibleText(el)));
  const innermost = holders.filter((el) => !holders.some((other) => other !== el && el.contains(other)));

  let figures = 0;
  for (const found of innermost) {
    if (figures >= MAX_FIGURES) break;
    // A figure's holder is usually a child of the element carrying the
    // style, so the whole chain has to be checked — a collapsed FAQ or a
    // hidden pricing tab must not reach the agent as current.
    if (hiddenInChain(found, root)) continue;
    if (visibleText(found).length > FIGURE_MAX_TEXT_LEN) continue;

    let el = found;
    for (let climbed = 0; climbed < FIGURE_CLIMB_MAX; climbed++) {
      const parent = el.parentElement;
      if (!parent || !root.contains(parent)) break;
      const tag = parent.tagName.toLowerCase();
      if (tag === 'body' || tag === 'html') break;
      if (visibleText(parent).length > FIGURE_MAX_TEXT_LEN) break;
      // The stops that matter: don't swallow prose we already have,
      // don't merge two figures, and don't cross a block boundary.
      if (proseEls.some((p) => parent.contains(p))) break;
      if (innermost.filter((h) => parent.contains(h)).length > 1) break;
      if (!onlyInlineChildren(parent)) break;
      el = parent;
    }

    const text = visibleText(el);
    if (text.length < FIGURE_MIN_TEXT_LEN) continue;
    // The prose pass may already hold this exact text (a `<p>` reading
    // "Plans from $290" inside a wrapper div). Don't say it twice.
    if (takenText.has(text)) continue;
    push(el, text);
    figures++;
  }

  // The contract carries MAX_ELEMENTS in total. When figures pushed past
  // it, drop trailing prose (footer-ish by document order) — never a figure.
  if (out.length > MAX_ELEMENTS) {
    const figuresAdded = out.length - proseCount;
    out.splice(Math.max(0, MAX_ELEMENTS - figuresAdded), out.length - MAX_ELEMENTS);
  }
  return out;
}

function isVisible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  const style = getComputedStyleSafe(el);
  if (style) {
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden') return false;
  }
  // Layout-based zero-size check is best-effort — jsdom doesn't compute
  // layout so offsets are always 0 in tests, and inline elements can
  // legitimately have 0 offset on real pages. Only treat as hidden when
  // computed style explicitly says so.
  return true;
}

function getComputedStyleSafe(el: HTMLElement): CSSStyleDeclaration | null {
  try {
    return (el.ownerDocument?.defaultView ?? window).getComputedStyle(el);
  } catch {
    return null;
  }
}

/**
 * Locate an element on the page whose visible text contains `needle`.
 * Used by the widget to handle `highlight-element` SSE events from
 * the agent runtime. Returns null when no match — caller should
 * silently no-op (over-eager LLM, page changed since the scrape, etc).
 *
 * Matching is punctuation-tolerant: both sides get non-alphanumeric
 * runs collapsed to single spaces before substring check. Without this
 * a page H1 reading "Your students already use AI. Shouldn't your
 * website?" doesn't match a needle like "your students already use AI
 * shouldn't your website" because the period in the haystack isn't in
 * the needle. The LLM often paraphrases or drops punctuation when
 * picking a substring to pass.
 *
 * When multiple elements match, prefer heading tags (h1 > h2 > ...) >
 * a/button > p/li/div. Without ranking, a `<p>` that happens to
 * paraphrase the headline wins over the actual `<h1>` just because it
 * appears later in document order or has more matching text.
 */
const TAG_PRIORITY: Record<string, number> = {
  h1: 100,
  h2: 90,
  h3: 80,
  h4: 70,
  h5: 60,
  h6: 50,
  button: 40,
  a: 30,
  li: 20,
  p: 10,
};

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function findElementByText(needle: string, root?: Element | Document): HTMLElement | null {
  if (typeof window === 'undefined' || !needle.trim()) return null;
  const r = root ?? document;
  const candidates = r.querySelectorAll<HTMLElement>(
    Array.from(SCRAPED_TAGS).join(','),
  );
  const target = normalize(needle);
  if (!target) return null;

  let best: { el: HTMLElement; score: number } | null = null;
  for (const el of candidates) {
    if (!isVisible(el)) continue;
    const text = normalize(el.textContent ?? '');
    if (!text.includes(target)) continue;

    const tag = el.tagName.toLowerCase();
    const tagScore = TAG_PRIORITY[tag] ?? 0;
    // Tighter match (less excess text around the substring) ranks higher
    // within the same tag class — so a 10-char H1 wins over a 200-char
    // <p> that quotes the H1 inline.
    const lengthScore = Math.max(0, 100 - (text.length - target.length));
    const score = tagScore * 1000 + lengthScore;
    if (!best || score > best.score) {
      best = { el, score };
    }
  }
  return best?.el ?? null;
}
